import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./Impact.css";

/*
 * Page d'accueil — Impact / performance
 * Le bloc graphique met désormais en avant 4 statistiques commerciales
 * directement lisibles, en cohérence avec la maquette premium.
 */
const CONTENT = {
  fr: {
    eyebrow: "Des résultats concrets pour le terrain",
    titleFirst: "Des données utiles.",
    titleSecond: "Des décisions plus rapides.",
    intro:
      "La valeur n'est pas dans le volume de données, mais dans la capacité de vos équipes à les exploiter.",
    chartTitle: "Impact mesurable",
    chartTag: "Statistiques clés",
    chartCaption: "Lecture rapide des bénéfices opérationnels.",
    metrics: [
      {
        label: "Temps d'intervention réduits",
        value: 30,
        displayValue: "-30%"
      },
      {
        label: "Arrêts diminués",
        value: 40,
        displayValue: "-40%"
      },
      {
        label: "Défaillances anticipées",
        value: 80,
        displayValue: "80%"
      },
      {
        label: "Coûts de maintenance réduits",
        value: 20,
        displayValue: "-20%"
      }
    ],
    stages: [
      {
        tag: "01 · Comprendre",
        title: "Repérez plus tôt",
        text: "Identifiez les anomalies et les événements qui demandent de l'attention."
      },
      {
        tag: "02 · Préparer",
        title: "Intervenez avec contexte",
        text: "Accédez aux informations utiles avant de vous déplacer."
      },
      {
        tag: "03 · Optimiser",
        title: "Améliorez le suivi",
        text: "Priorisez les actions et réduisez les démarches inutiles."
      },
      {
        tag: "04 · Mesurer",
        title: "Suivez vos résultats",
        text: "Valorisez vos indicateurs lorsque les mesures terrain sont disponibles."
      }
    ]
  },
  en: {
    eyebrow: "Practical value for your teams",
    titleFirst: "Useful insights.",
    titleSecond: "Faster decisions.",
    intro:
      "Value doesn't lie in the volume of data, but in your teams' ability to make use of it.",
    chartTitle: "Measured impact",
    chartTag: "Key statistics",
    chartCaption: "A quick view of operational benefits.",
    metrics: [
      {
        label: "Faster intervention times",
        value: 30,
        displayValue: "+30%"
      },
      {
        label: "Downtime reduced",
        value: 40,
        displayValue: "+40%"
      },
      {
        label: "Failures identified early",
        value: 80,
        displayValue: "80%"
      },
      {
        label: "Maintenance costs reduced",
        value: 20,
        displayValue: "+20%"
      }
    ],
    stages: [
      {
        tag: "01 · Understand",
        title: "Spot issues earlier",
        text: "Identify anomalies and events that require attention."
      },
      {
        tag: "02 · Prepare",
        title: "Act with context",
        text: "Access useful information before heading on-site."
      },
      {
        tag: "03 · Optimize",
        title: "Improve follow-up",
        text: "Prioritize actions and reduce unnecessary steps."
      },
      {
        tag: "04 · Measure",
        title: "Track your results",
        text: "Review your indicators when field measurements are available."
      }
    ]
  }
};

function Stage({ tag, title, text, hasArrow }) {
  return (
    <li className="unitech-impact__stage">
      <p className="unitech-impact__stage-tag">{tag}</p>
      <h3 className="unitech-impact__stage-title">{title}</h3>
      <p className="unitech-impact__stage-text">{text}</p>
      {hasArrow && (
        <svg
          className="unitech-impact__stage-arrow"
          viewBox="0 0 28 24"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M2 12h23m-7-7 7 7-7 7"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </li>
  );
}

function Metric({ label, value, displayValue, index }) {
  return (
    <li className="unitech-impact__metric">
      <div className="unitech-impact__metric-head">
        <span className="unitech-impact__metric-label">{label}</span>
        <span className="unitech-impact__metric-value">{displayValue}</span>
      </div>
      <div
        className="unitech-impact__metric-track"
        role="img"
        aria-label={`${label} ${displayValue}`}
      >
        <span
          className="unitech-impact__metric-fill"
          style={{ "--target-width": `${value}%`, animationDelay: `${index * 90}ms` }}
          aria-hidden="true"
        />
      </div>
    </li>
  );
}

export default function Impact() {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef(null);
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const fallback = CONTENT[language] || CONTENT.fr;

  const read = (path, defaultValue) =>
    t(`impact.premium.${path}`, { defaultValue });

  const stages = fallback.stages.map((stage, index) => ({
    tag: read(`stages.${index}.tag`, stage.tag),
    title: read(`stages.${index}.title`, stage.title),
    text: read(`stages.${index}.text`, stage.text)
  }));

  const metrics = fallback.metrics.map((metric, index) => ({
    label: read(`metrics.${index}.label`, metric.label),
    value: Number(read(`metrics.${index}.value`, metric.value)),
    displayValue: read(`metrics.${index}.displayValue`, metric.displayValue)
  }));

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some(({ isIntersecting }) => isIntersecting)) {
          node.classList.add("unitech-impact--revealed");
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="unitech-impact"
      ref={sectionRef}
      aria-labelledby="unitech-impact-title"
    >
      <div className="unitech-impact__container">
        <div className="unitech-impact__top">
          <header className="unitech-impact__header">
            <p className="unitech-impact__eyebrow">
              {read("eyebrow", fallback.eyebrow)}
            </p>
            <h2 id="unitech-impact-title" className="unitech-impact__title">
              <span>{read("titleFirst", fallback.titleFirst)}</span>
              <span>{read("titleSecond", fallback.titleSecond)}</span>
            </h2>
            <p className="unitech-impact__intro">{read("intro", fallback.intro)}</p>
          </header>

          <aside
            className="unitech-impact__chart"
            aria-label={read("chartTag", fallback.chartTag)}
          >
            <div className="unitech-impact__chart-head">
              <div className="unitech-impact__chart-heading">
                <span className="unitech-impact__chart-title">
                  {read("chartTitle", fallback.chartTitle)}
                </span>
                <p className="unitech-impact__chart-caption">
                  {read("chartCaption", fallback.chartCaption)}
                </p>
              </div>
              <span className="unitech-impact__chart-label">
                {read("chartTag", fallback.chartTag)}
              </span>
            </div>

            <ul className="unitech-impact__metrics">
              {metrics.map((metric, index) => (
                <Metric key={index} {...metric} index={index} />
              ))}
            </ul>
          </aside>
        </div>

        <ol className="unitech-impact__stages">
          {stages.map((stage, index) => (
            <Stage key={index} {...stage} hasArrow={index < stages.length - 1} />
          ))}
        </ol>
      </div>
    </section>
  );
}
